import { Alert, Button, Skeleton, Typography } from 'antd';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMessages, useSendMessageMutation } from '@entities/chat';
import { extractErrorMessage } from '@shared/api';
import { MessageBubble } from './MessageBubble';
import { Thinking } from './Thinking';
import { MessageInput } from './MessageInput';

const { Text } = Typography;

interface Props {
  threadId: string;
}

/**
 * Главная панель чата для конкретного треда.
 *
 * Высота фиксированная — 60vh — чтобы поле ввода всегда было видно
 * и не приходилось скроллить страницу для отправки.
 */
export function ChatPanel({ threadId }: Props) {
  const { data: messages, isLoading, error } = useMessages(threadId);
  const sendMutation = useSendMessageMutation(threadId);
  const navigate = useNavigate();

  const scrollRef = useRef<HTMLDivElement>(null);

  // Скроллим вниз при появлении нового сообщения
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages?.length]);

  const handleSend = (content: string) => {
    sendMutation.mutate({ content });
  };

  const sendError = sendMutation.error ? extractErrorMessage(sendMutation.error) : null;
  // V2-1: тест-чат платный. 402 — баланс исчерпан.
  const noMoney =
    (sendMutation.error as { response?: { status?: number } } | null)?.response?.status === 402;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '60vh',
        minHeight: 400,
        border: '1px solid #E5E7EB',
        borderRadius: 8,
        overflow: 'hidden',
        background: '#FFFFFF',
      }}
    >
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 16px 4px',
        }}
      >
        {isLoading && <Skeleton active paragraph={{ rows: 4 }} />}

        {error && (
          <Alert
            type="error"
            showIcon
            message="Не удалось загрузить историю"
            description={extractErrorMessage(error)}
          />
        )}

        {messages && messages.length === 0 && !sendMutation.isPending && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary">
              Напишите ассистенту что-нибудь, чтобы проверить как он отвечает.
            </Text>
          </div>
        )}

        {messages?.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}

        {sendMutation.isPending && (
          <div style={{ marginBottom: 12, paddingLeft: 14 }}>
            <Thinking />
          </div>
        )}

        {sendError && (
          <Alert
            type={noMoney ? 'warning' : 'error'}
            showIcon
            message={sendError}
            action={
              noMoney ? (
                <Button size="small" type="primary" onClick={() => navigate('/billing')}>
                  Пополнить
                </Button>
              ) : undefined
            }
            style={{ marginBottom: 12 }}
          />
        )}
      </div>

      <MessageInput onSend={handleSend} isPending={sendMutation.isPending} />
    </div>
  );
}
